/**
 * 🌟 Fleet Partners Tab - Premium Partner Agreement Management
 * Displays and manages premium fleet partner agreements
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Add,
  Visibility,
  CheckCircle,
  Schedule,
  Cancel,
  Block,
  Star,
  TrendingUp,
} from '@mui/icons-material';
import { premiumPartnerService, Agreement } from '@/services/premiumPartnerService';
import { CreateAgreementDialog } from './CreateAgreementDialog';
import { AgreementDetailDialog } from './AgreementDetailDialog';
import toast from 'react-hot-toast';

export const FleetPartnersTab: React.FC = () => {
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending'>('active');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);

  useEffect(() => {
    loadAgreements();
  }, [statusFilter]);

  const loadAgreements = async () => {
    setLoading(true);
    try {
      const response = await premiumPartnerService.getAgreements({
        status: statusFilter === 'all' ? undefined : statusFilter as any
      });

      if (response.success && response.data) {
        setAgreements(response.data.agreements);
      } else {
        // Backend error - show empty state gracefully
        console.error('Backend error loading agreements:', response.error);
        setAgreements([]);

        // Only show error toast if it's NOT a 500 error (backend might not be ready yet)
        const is500Error = response.error?.includes('500') ||
                          response.error?.includes('Internal server error') ||
                          response.error?.includes('HTTP error');

        if (!is500Error) {
          toast.error(response.error || 'Failed to load agreements');
        } else {
        }
      }
    } catch (error) {
      console.error('Error loading agreements:', error);
      setAgreements([]);

      // Only show error toast if it's NOT a network/500 error
      const errorMsg = error.toString();
      const is500Error = errorMsg.includes('500') ||
                        errorMsg.includes('Internal server error') ||
                        errorMsg.includes('HTTP error');

      if (!is500Error) {
        toast.error('Failed to load fleet partner agreements');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (agreementId: string) => {
    const adminUserId = localStorage.getItem('userId');
    if (!adminUserId) {
      toast.error('Admin user ID not found');
      return;
    }

    if (!confirm('Approve this agreement? This will upgrade the host to Premium tier and activate custom rates.')) {
      return;
    }

    try {
      const response = await premiumPartnerService.approveAgreement(
        agreementId,
        adminUserId,
        undefined,
        'Approved via admin dashboard'
      );

      if (response.success) {
        toast.success('Agreement approved successfully!');
        loadAgreements();
      } else {
        toast.error(response.error || 'Failed to approve agreement');
      }
    } catch (error) {
      console.error('Error approving agreement:', error);
      toast.error('An error occurred while approving the agreement');
    }
  };

  const handleViewDetails = (agreement: Agreement) => {
    setSelectedAgreement(agreement);
    setViewDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      case 'terminated': return 'default';
      case 'expired': return 'warning';
      default: return 'default';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'premium': return <Star sx={{ fontSize: 16, color: 'gold' }} />;
      case 'fleet': return <TrendingUp sx={{ fontSize: 16, color: 'primary.main' }} />;
      default: return null;
    }
  };

  const stats = {
    total: agreements.length,
    active: agreements.filter(a => a.status === 'active').length,
    pending: agreements.filter(a => a.status === 'pending').length,
    premium: agreements.filter(a => a.host.partnerTier === 'premium').length,
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Agreements
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {stats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Partners
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {stats.premium}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Premium Tier
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Fleet Partner Agreements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage premium partner agreements with custom commission rates
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          New Agreement
        </Button>
      </Box>

      {/* Filter Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={statusFilter} onChange={(e, val) => setStatusFilter(val)}>
          <Tab label={`All (${stats.total})`} value="all" />
          <Tab label={`Active (${stats.active})`} value="active" />
          <Tab label={`Pending (${stats.pending})`} value="pending" />
        </Tabs>
      </Box>

      {/* Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Premium Partners</strong> get custom commission rates and priority support. Approving an agreement
          upgrades the host to Premium tier and applies custom booking fees automatically.
        </Typography>
      </Alert>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : agreements.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No agreements found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            {statusFilter === 'all'
              ? 'Create your first premium partner agreement to get started'
              : `No ${statusFilter} agreements at the moment`
            }
          </Typography>
          <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Note:</strong> If you're seeing this and the backend has been deployed, the database migration might need to be run:
            </Typography>
            <Typography variant="caption" component="pre" sx={{ mt: 1, display: 'block' }}>
              psql $DATABASE_URL -f migrations/create_premium_partner_system.sql
            </Typography>
          </Alert>
          {statusFilter === 'all' && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mt: 3, textTransform: 'none' }}
            >
              Create First Agreement
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Partner</TableCell>
                <TableCell>Tier & Vehicles</TableCell>
                <TableCell>Commission Rates</TableCell>
                <TableCell>Contract Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agreements.map((agreement) => (
                <TableRow key={agreement.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {agreement.host.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {agreement.host.email}
                      </Typography>
                      {agreement.accountManager && (
                        <Typography variant="caption" display="block" color="primary.main">
                          Manager: {agreement.accountManager.firstName} {agreement.accountManager.lastName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {getTierIcon(agreement.host.partnerTier)}
                      <Chip
                        label={agreement.host.partnerTier}
                        size="small"
                        color={agreement.host.partnerTier === 'premium' ? 'secondary' : 'default'}
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {agreement.host.vehicleCount} vehicles
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Platform: {agreement.commission.platformFeePercentage}%
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Booking: {agreement.commission.bookingFeePercentage}%
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {new Date(agreement.contract.startDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      to {agreement.contract.endDate ? new Date(agreement.contract.endDate).toLocaleDateString() : 'Ongoing'}
                    </Typography>
                    {agreement.contract.autoRenewal && (
                      <Chip label="Auto-renew" size="small" variant="outlined" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={agreement.status}
                      color={getStatusColor(agreement.status) as any}
                      size="small"
                      variant={agreement.status === 'active' ? 'filled' : 'outlined'}
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(agreement)} color="primary">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {agreement.status === 'pending' && (
                        <Tooltip title="Approve Agreement">
                          <IconButton size="small" onClick={() => handleApprove(agreement.id)} color="success">
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogs */}
      <CreateAgreementDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          loadAgreements();
        }}
      />

      {selectedAgreement && (
        <AgreementDetailDialog
          open={viewDialogOpen}
          agreement={selectedAgreement}
          onClose={() => {
            setViewDialogOpen(false);
            setSelectedAgreement(null);
          }}
          onUpdate={loadAgreements}
        />
      )}
    </Box>
  );
};
